import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, useParams, Link, useNavigate } from 'react-router-dom';
import { Settings, Trash2, ArrowLeft, FolderPlus, Mail, Send, FolderInput, Download, Printer } from 'lucide-react';
import ViewMotivationLetterModal from '../components/Employer/ViewMotivationLetterModal';
import useConfirmDialog from '../hooks/useConfirmDialog';
import SelectAllToggle from '../components/Common/SelectAllToggle';
import './SelectionDetail.scss';

interface Selection {
  id: number;
  user_id: number;
  folder_id?: number;
  name: string;
  description?: string;
  folder_name?: string;
  candidates_count: number;
}

interface Candidate {
  id: number;
  selection_id: number;
  cv_id: number;
  notes?: string;
  added_at: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  location?: string;
  title?: string;
  profile_image_url?: string;
  user_id?: number;
  birth_date?: string;
  work_experience?: any[];
  job_preferences?: any;
}

/**
 * SelectionDetail Page
 * 
 * Shows all candidates within a specific selection (atranka).
 * Displays candidate cards with ratings, actions, and quick access to CVs and motivation letters.
 */
const SelectionDetail: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { confirm, ConfirmDialogNode } = useConfirmDialog();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [selection, setSelection] = useState<Selection | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCandidates, setSelectedCandidates] = useState<Set<number>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [showMotivationModal, setShowMotivationModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [openSettingsMenu, setOpenSettingsMenu] = useState<number | null>(null);

  const fetchSelection = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/employer-selections/selections/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setSelection(result.data);
      }
    } catch (error) {
      console.error('Error fetching selection:', error);
    }
  }, [id]);

  const fetchCandidates = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/employer-selections/selections/${id}/candidates`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setCandidates(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isAuthenticated && user?.user_type === 'employer' && id) {
      fetchSelection();
      fetchCandidates();
    }
  }, [isAuthenticated, user, id, fetchSelection, fetchCandidates]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.settings-dropdown-container')) {
        setOpenSettingsMenu(null);
      }
    };

    if (openSettingsMenu !== null) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openSettingsMenu]);

  const handleRemoveCandidate = async (candidateId: number) => {
    if (!await confirm('Ar tikrai norite pašalinti šį kandidatą iš atrankos?')) return;

    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/employer-selections/candidates/${candidateId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        fetchCandidates();
        fetchSelection(); // Update count
      }
    } catch (error) {
      console.error('Error removing candidate:', error);
    }
  };

  const toggleSelectCandidate = (candidateId: number) => {
    const newSelected = new Set(selectedCandidates);
    if (newSelected.has(candidateId)) {
      newSelected.delete(candidateId);
    } else {
      newSelected.add(candidateId);
    }
    setSelectedCandidates(newSelected);
    setSelectAll(newSelected.size === candidates.length);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedCandidates(new Set());
      setSelectAll(false);
    } else {
      setSelectedCandidates(new Set(candidates.map(c => c.id)));
      setSelectAll(true);
    }
  };

  const handleViewCV = (cvId: number) => {
    const currentUrl = window.location.pathname + window.location.search;
    const returnUrl = encodeURIComponent(currentUrl);
    navigate(`/candidate/${cvId}?mode=employer&returnUrl=${returnUrl}`);
  };

  const handleViewMotivationLetter = (candidate: Candidate) => {
    setSelectedCandidate(candidate);
    setShowMotivationModal(true);
  };

  const toggleSettingsMenu = (candidateId: number) => {
    if (openSettingsMenu === candidateId) {
      setOpenSettingsMenu(null);
    } else {
      setOpenSettingsMenu(candidateId);
    }
  };

  const handleReplyToCandidate = (candidate: Candidate) => {
    // TODO: Open reply modal or navigate to messaging
    console.log('Reply to candidate:', candidate);
    setOpenSettingsMenu(null);
  };

  const handleSendEmail = (candidate: Candidate) => {
    window.location.href = `mailto:${candidate.email}`;
    setOpenSettingsMenu(null);
  };

  const handleMoveCV = (candidate: Candidate) => {
    // TODO: Open move to another selection modal
    console.log('Move CV:', candidate);
    setOpenSettingsMenu(null);
  };

  const handleDownloadCV = (candidate: Candidate) => {
    // TODO: Download CV as PDF
    console.log('Download CV:', candidate);
    setOpenSettingsMenu(null);
  };

  const handlePrintCV = (candidate: Candidate) => {
    // TODO: Open print dialog for CV
    window.open(`/candidate/${candidate.cv_id}/print?mode=employer`, '_blank');
    setOpenSettingsMenu(null);
  };

  const getAge = (birthDate?: string) => {
    if (!birthDate) return 34;
    const today = new Date();
    const birth = new Date(birthDate);
    const age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      return age - 1;
    }
    return age;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('lt-LT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // Show loading state while auth is being checked
  if (isLoading) {
    return (
      <div className="selection-detail-container">
        <div className="loading">Kraunama...</div>
      </div>
    );
  }

  // Redirect non-employers
  if (!isAuthenticated || user?.user_type !== 'employer') {
    return <Navigate to="/" />;
  }

  if (loading) {
    return (
      <div className="selection-detail-page">
        <div className="container">
          <div className="loading">Kraunama...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="selection-detail-page">
      {ConfirmDialogNode}
      <div className="container">
        <div className="breadcrumb">
          <Link to="/">Titulinis</Link>
          <span>&gt;</span>
          <Link to="/atrankos">Atrankos</Link>
          {selection?.folder_name && (
            <>
              <span>&gt;</span>
              <span>{selection.folder_name}</span>
            </>
          )}
          <span>&gt;</span>
          <span className="current">{selection?.name}</span>
        </div>

        <div className="page-header">
          <div className="header-left">
            <button className="btn-back" onClick={() => navigate('/atrankos')}>
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1>{selection?.name}</h1>
              {selection?.description && <p className="subtitle">{selection.description}</p>}
            </div>
          </div>
          <button className="btn-settings">
            <Settings size={20} />
          </button>
        </div>

        <div className="filters-bar">
          <div className="filter-left">
            <button className="btn-select-all" onClick={handleSelectAll}>
              <input 
                type="checkbox" 
                checked={selectAll}
                onChange={handleSelectAll}
              />
              Pasirinkti 4 kriterijami
            </button>
            <SelectAllToggle
              selectedCount={selectedCandidates.size}
              totalCount={candidates.length}
              onToggle={handleSelectAll}
            />
            <select className="select-action" disabled={selectedCandidates.size === 0}>
              <option value="">Pasirinkti veiksmą</option>
              <option value="delete">Ištrinti pažymėtus</option>
              <option value="export">Eksportuoti</option>
            </select>
          </div>
          <button className="btn-edit-layout">
            Redaguoti laukų išdėstymą
          </button>
        </div>

        {candidates.length === 0 ? (
          <div className="empty-state">
            <FolderPlus size={64} />
            <p>Šioje atrankoje dar nėra kandidatų</p>
            <p className="hint">Pridėkite kandidatus iš CV peržiūros puslapio</p>
          </div>
        ) : (
          <div className="candidates-grid">
            {candidates.map((candidate) => (
              <div key={candidate.id} className="candidate-card">
                <div className="card-header">
                  <input
                    type="checkbox"
                    checked={selectedCandidates.has(candidate.id)}
                    onChange={() => toggleSelectCandidate(candidate.id)}
                    className="candidate-checkbox"
                  />
                  <div className="card-actions">
                    <button 
                      className="btn-icon"
                      title="Pridėti į kitą atranką"
                    >
                      <FolderPlus size={16} />
                    </button>
                    <div className="settings-dropdown-container">
                      <button 
                        className={`btn-icon ${openSettingsMenu === candidate.id ? 'active' : ''}`}
                        title="Nustatymai"
                        onClick={() => toggleSettingsMenu(candidate.id)}
                      >
                        <Settings size={16} />
                      </button>
                      
                      {openSettingsMenu === candidate.id && (
                        <div className="settings-dropdown">
                          <button onClick={() => handleReplyToCandidate(candidate)}>
                            <Mail size={18} />
                            Atsakyti kandidatui
                          </button>
                          <button onClick={() => handleSendEmail(candidate)}>
                            <Send size={18} />
                            Siųsti el. paštą
                          </button>
                          <button onClick={() => handleMoveCV(candidate)}>
                            <FolderInput size={18} />
                            Perkelti CV
                          </button>
                          <button onClick={() => handleDownloadCV(candidate)}>
                            <Download size={18} />
                            Atsįusti CV
                          </button>
                          <button onClick={() => handlePrintCV(candidate)}>
                            <Printer size={18} />
                            Spausdinti CV
                          </button>
                        </div>
                      )}
                    </div>
                    <button 
                      className="btn-icon btn-delete"
                      onClick={() => handleRemoveCandidate(candidate.id)}
                      title="Pašalinti"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <div className="card-body">
                  <div className="profile-photo">
                    <img 
                      src={`https://ui-avatars.com/api/?name=${candidate.first_name}+${candidate.last_name}&background=7a7a7a&color=fff&size=200`}
                      alt={`${candidate.first_name} ${candidate.last_name}`}
                    />
                  </div>

                  <h3 className="candidate-name">
                    {candidate.first_name} {candidate.last_name.charAt(0)}.
                  </h3>
                  <p className="candidate-meta">
                    Vardenis {getAge(candidate.birth_date)} m. {candidate.location || 'Vilnius'}
                  </p>

                  <div className="candidate-info">
                    <div className="info-section">
                      <strong>Darbo patirtis (paskutinė pozicija)</strong>
                      <p>{candidate.title || 'UAB Green, pardavimų vadybininkas (1metai ir 2 mėnesiai)'}</p>
                    </div>

                    <div className="info-section">
                      <strong>Ieškomas darbas</strong>
                      <p>{candidate.job_preferences?.position || 'Projektų vadovas, vadovas, administravimas, pilna darbo diena, pastovus darbas, Vilnius'}</p>
                    </div>

                    <div className="info-section">
                      <strong>Pageidaujamas atlyginimas:</strong>
                      <span className="salary">{candidate.job_preferences?.salary || '500 €'}</span>
                    </div>

                    <div className="info-section last-active">
                      <span>Paskutinis aktyvumas {formatDate(candidate.added_at)}</span>
                    </div>
                  </div>

                  <div className="card-rating">
                    <label>Įvertinkite kandidatą:</label>
                    <select className="rating-select">
                      <option value="">Tinkamas</option>
                      <option value="excellent">Puikus</option>
                      <option value="good">Geras</option>
                      <option value="average">Vidutinis</option>
                      <option value="poor">Netinkamas</option>
                    </select>
                  </div>

                  <div className="card-actions-bottom">
                    <div className="motivation-letter-section">
                      <label>Motyvacinis laiškas:</label>
                      <button 
                        className="btn-view-letter"
                        onClick={() => handleViewMotivationLetter(candidate)}
                      >
                        ŽIŪRĖTI
                      </button>
                    </div>

                    <button 
                      className="btn-view-cv"
                      onClick={() => handleViewCV(candidate.cv_id)}
                    >
                      ŽIŪRĖTI CV →
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Motivation Letter Modal */}
        {showMotivationModal && selectedCandidate && (
          <ViewMotivationLetterModal
            userId={selectedCandidate.user_id || 0}
            onClose={() => {
              setShowMotivationModal(false);
              setSelectedCandidate(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default SelectionDetail;

